import React, { useCallback, useEffect, useRef } from 'react';
import { useUrlParams } from '../../hooks/useUrlParams';
import TaskFilters from './TaskFilters';
import TaskToolbar from './TaskToolbar';
import TasksBoard from './TasksBoard';
import TasksDynamicsTab from './TasksDynamicsTab';
import './TasksSection.css';
import ToolHelpButton from '../../components/ToolHelpButton';

const TABS = [
    { id: 'tasks', label: 'Задачи' },
    { id: 'dynamics', label: 'Динамика' },
];

// AI-задачи пока не запускаем: кнопка скрыта, код и бэкенд оставлены на будущее.
const AI_TASKS_ENABLED = false;

function TasksSection({
    onCreateTask,
    onExecuteAiTasks,
    tasksView,
    onViewChange,
    onPreset,
    taskFilters,
    setTaskFilters,
    activeProjectId,
    visibleWorkspaces,
    companyUsers,
    getUserLabel,
    taskStatusLabels,
    taskTags,
    marketplaces,
    deals,
    onRefresh,
    tasksBusy,
    tasks,
    getWorkspaceById,
    onOpenTask,
    kanbanColumns,
    kanbanGroup,
    getInitials,
    onDragStart,
    onDrop,
    companyId,
}) {
    // Вкладка живёт в адресе (?sub=dynamics): F5, ссылка и «Назад» ведут туда же.
    const { get: getUrlParam, update: updateUrlParams } = useUrlParams();
    const urlTab = getUrlParam('sub');
    const tab = TABS.some((t) => t.id === urlTab) ? urlTab : 'tasks';
    // Проект в задачах один — текущий (?project): фильтр и «Динамика» следуют за ним,
    // выбор проекта в фильтре меняет его же. «Все» в фильтре — временный показ всех проектов.
    useEffect(() => {
        if (!activeProjectId) return;
        setTaskFilters((prev) => (prev.workspaceId === activeProjectId ? prev : { ...prev, workspaceId: activeProjectId }));
    }, [activeProjectId]); // eslint-disable-line
    // Фильтры и вид: из адреса при входе, в адрес — при изменении (без новых записей в истории).
    const hydratedRef = useRef(false);
    useEffect(() => {
        if (hydratedRef.current) return;
        hydratedRef.current = true;
        const raw = getUrlParam('tf');
        if (raw) {
            try {
                const parsed = JSON.parse(raw);
                if (parsed && typeof parsed === 'object') {
                    const { workspaceId, ...rest } = parsed; // проект ведёт ?project
                    setTaskFilters((prev) => ({ ...prev, ...rest }));
                }
            } catch { /* битая ссылка — фильтры по умолчанию */ }
        }
        const urlView = getUrlParam('view');
        if (urlView === 'kanban' && tasksView !== 'kanban') onViewChange('kanban');
    }, []); // eslint-disable-line
    useEffect(() => {
        if (!hydratedRef.current) return;
        const active = {};
        Object.entries(taskFilters || {}).forEach(([key, value]) => {
            if (key === 'workspaceId') return;
            if (value !== '' && value !== false && value != null) active[key] = value;
        });
        const encoded = Object.keys(active).length ? JSON.stringify(active) : '';
        updateUrlParams({ tf: encoded, view: tasksView === 'kanban' ? 'kanban' : '' });
    }, [taskFilters, tasksView]); // eslint-disable-line
    const setTaskFiltersAndProject = useCallback((next) => {
        setTaskFilters(next);
        if (next && next.workspaceId && next.workspaceId !== activeProjectId) {
            updateUrlParams({ project: next.workspaceId }, { push: true });
        }
    }, [setTaskFilters, activeProjectId, updateUrlParams]);
    const setTab = (id) => updateUrlParams({ sub: id === 'tasks' ? '' : id }, { push: true });

    return (
        <div className="panel tasks">
            <div className="panel__head">
                <div>
                    <h3>Задачи<ToolHelpButton toolKey="tasks" /></h3>
                    <p className="muted">
                        Создавайте задачи, назначайте участников и привязывайте кабинеты.
                    </p>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <div className="tasks-tabs">
                        {TABS.map((t) => (
                            <button
                                key={t.id}
                                type="button"
                                className={`tasks-tabs__btn${tab === t.id ? ' active' : ''}`}
                                onClick={() => setTab(t.id)}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                    {tab === 'tasks' && (
                        <>
                            <button
                                type="button"
                                className="ghost"
                                onClick={onCreateTask}
                            >
                                Создать задачу
                            </button>
                            {AI_TASKS_ENABLED && (
                                <button
                                    type="button"
                                    className="ghost"
                                    onClick={onExecuteAiTasks}
                                    disabled={tasksBusy}
                                >
                                    Выполнить AI задачи
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>

            {tab === 'tasks' && (
                <>
                    <TaskToolbar
                        tasksView={tasksView}
                        onViewChange={onViewChange}
                        onPreset={onPreset}
                    />
                    <TaskFilters
                        taskFilters={taskFilters}
                        setTaskFilters={setTaskFiltersAndProject}
                        lockedWorkspaceName=""
                        visibleWorkspaces={visibleWorkspaces}
                        companyUsers={companyUsers}
                        getUserLabel={getUserLabel}
                        taskStatusLabels={taskStatusLabels}
                        taskTags={taskTags}
                        marketplaces={marketplaces}
                        deals={deals}
                        onRefresh={onRefresh}
                    />
                    <TasksBoard
                        heading="Список задач"
                        tasksView={tasksView}
                        tasksBusy={tasksBusy}
                        tasks={tasks}
                        taskStatusLabels={taskStatusLabels}
                        getWorkspaceById={getWorkspaceById}
                        getUserLabel={getUserLabel}
                        companyUsers={companyUsers}
                        onOpenTask={onOpenTask}
                        kanbanColumns={kanbanColumns}
                        kanbanGroup={kanbanGroup}
                        getInitials={getInitials}
                        onDragStart={onDragStart}
                        onDrop={onDrop}
                    />
                </>
            )}

            {tab === 'dynamics' && (
                <TasksDynamicsTab
                    workspaceId={activeProjectId}
                    companyId={companyId}
                    marketplaces={marketplaces}
                    visibleWorkspaces={visibleWorkspaces}
                />
            )}
        </div>
    );
}

export default TasksSection;
